import React, { useMemo, useState } from 'react';
import { Calendar } from 'react-date-range';
import { Button, Dropdown, DropdownList } from '@cokitchen/cokitchen-components';
import { ArrowDownIcon } from 'cokitchen-icons';
import LocalModal from 'components/base/local-modal';
import moment from 'moment';
import { BrandType } from 'models/brand.model';
import SelectDropdown from 'components/base/select-dropdown';

type Props = {
  brand: BrandType;
  open: boolean;
  onClose: () => void;
  onConfirm: (data: { preorder_delivery_time: string; preorder_delivery_date: string }) => void;
};

const PreorderDeliveryModal = (props: Props) => {
  const { brand, open, onClose, onConfirm } = props;
  const startofDay = moment()
    .startOf('hour')
    .add(+(brand.preorder_duration_in_hours ?? 0) + (moment().minute() > 0 ? 1 : 0), 'hours');
  const endofDay = moment().set({ hour: 18, minute: 0 });
  const defaultDate = startofDay.isSameOrAfter(endofDay)
    ? moment().startOf('day').add(1, 'day').toDate()
    : startofDay.toDate();
  const [date, setDate] = useState(defaultDate);
  const [time, setTime] = useState('');
  const [showDateModal, setShowDateModal] = useState(false);

  const changeDate = (date: Date) => {
    if (moment(date).format('YYYY-MM-DD') === startofDay.format('YYYY-MM-DD'))
      setDate(startofDay.toDate());
    else setDate(date);
  };

  const timeOptions = useMemo(() => {
    const startHour = moment(date).startOf('hour');
    if (startHour.hour() < 9) startHour.set({ hour: 9, minute: 0 });
    const endHour = moment(date).set({ hour: 18, minute: 0 });
    const options: { id: string; name: string }[] = [];
    while (startHour.isBefore(endHour) && startHour.hour() < 18) {
      const minHour = startHour.format('h:mmA');
      const maxHour = startHour.add(1, 'hour').format('h:mmA');
      options.push({ id: `${minHour} - ${maxHour}`, name: `${minHour} - ${maxHour}` });
    }
    setTime(options[0]?.id ?? '');
    return options;
  }, [date]);

  return (
    <LocalModal isOpen={open} onRequestClose={onClose} title='When do you want it delivered?'>
      <div className='w-full flex flex-col gap-8 mt-4'>
        <div>
          <p className='text-[#2E3031] text-sm font-medium'>
            Choose a date for your order to be delivered
          </p>
          <Dropdown
            visible={showDateModal}
            onClickOutside={() => setShowDateModal(false)}
            content={
              <div
                className='w-full flex justify-between h-[52px] bg-[#FAFAFA] py-[15px] px-[14px] border-b-[2px] border-b-[#D5D6D7]'
                onClick={() => setShowDateModal(!showDateModal)}>
                <p>{date ? moment(date).format('dddd, Do MMMM YYYY') : 'Select a date'}</p>
                <ArrowDownIcon className='w-5 h-5' />
              </div>
            }>
            <DropdownList className='!p-0 !max-h-fit'>
              <Calendar
                date={date}
                onChange={changeDate}
                showDateDisplay={showDateModal}
                className='calendar'
                minDate={defaultDate}
              />
            </DropdownList>
          </Dropdown>
        </div>

        <div>
          <p className='text-[#2E3031] text-sm font-medium mb-2'>
            Choose a time frame for your order to be delivered
          </p>
          <SelectDropdown
            placeholder='Select a time frame'
            value={time}
            setValue={(v) => setTime(v.name || v)}
            options={timeOptions}
          />
        </div>

        <div className='w-full flex justify-end mt-4'>
          <Button
            variant='block'
            color='primary'
            className='h-[41px] w-[139px]'
            disabled={!time || !date}
            onClick={() => {
              onConfirm({
                preorder_delivery_time: time
                  .split(' - ')
                  .map((t) => moment(t, 'h:mmA').format('HH:mm'))
                  .join(' - '),
                preorder_delivery_date: moment(date).format('YYYY-MM-DD')
              });
            }}>
            Confirm
          </Button>
        </div>
      </div>
    </LocalModal>
  );
};

export default PreorderDeliveryModal;
